"use client";
import React from "react";
import { AbsoluteFill, Sequence, useCurrentFrame, useVideoConfig, interpolate } from "remotion";
import { loadFont } from "@remotion/google-fonts/NotoSansDevanagari";
import { HOLES_DATA_URI } from "./trimbakHolesData";
import { DevaKathaLS2 } from "./DevaKathaLS2";
import { DevaKathaLS5 } from "./DevaKathaLS5";

const { fontFamily } = loadFont("normal", { weights: ["400", "700", "800", "900"], subsets: ["devanagari", "latin"] });

// Jagannath Puri phrase overlays — Trimbakeshwar holes treatment. Top-left, yellow-prominent,
// per-word colour + line breaks, black drop-shadow, scattered-holes mask, word-by-word drop-in.

const PAD = 25;
const YELLOW = "#FFD60A";
const WHITE = "#FFFFFF";
const RED = "#FF3B2E";
const SH = "0 5px 14px rgba(0,0,0,0.9), 0 2px 5px rgba(0,0,0,0.95)";
const HOLES = HOLES_DATA_URI;

const BASE_FS = 66, BIG_FS = 92, SMALL_FS = 46;
const STAGGER = 0.16, DROP = 26, DUR = 0.32;
const LH = 1.18;
const LINE_GAP = -10;

type Word = { t: string; c?: "y" | "w" | "r"; big?: boolean; small?: boolean };
export type Phrase = { lines: Word[][]; start: number; out: number; lineGap?: number; lh?: number; ls?: string };
const col = (c?: string) => (c === "w" ? WHITE : c === "r" ? RED : YELLOW);

// 35 cards. start = exact word_timestamps_hindi anchor (verified). out = start + ~3.3.
export const JAG_PHRASES: Phrase[] = [
  { start: 0.00,    out: 5.00,    lines: [[{ t: "15", c: "w", big: true }, { t: "जून", c: "y" }, { t: "2015", c: "w" }], [{ t: "पूरे", small: true }, { t: "शहर", c: "y" }, { t: "में", small: true }, { t: "अँधेरा", small: true }]] },
  { start: 10.59,   out: 13.90,   lines: [[{ t: "बिजली", small: true }, { t: "कटौती", small: true }, { t: "नहीं", small: true }], [{ t: "एक", c: "r" }, { t: "आदेश", c: "r", big: true }]] },
  { start: 19.83,   out: 22.00,   lines: [[{ t: "अंदर", small: true }, { t: "सिर्फ", c: "y" }], [{ t: "4", c: "w", big: true }, { t: "लोग", c: "w" }]] },
  { start: 22.36,   out: 25.70,   lines: [[{ t: "आँखों", c: "w" }, { t: "पर", small: true }], [{ t: "पट्टी", c: "y", big: true }]] },
  { start: 42.79,   out: 46.10,   lines: [[{ t: "जिसने", small: true }, { t: "देखा", c: "w" }], [{ t: "ज़िंदा", c: "r", big: true }, { t: "नहीं", c: "r" }]] },
  { start: 58.16,   out: 61.50,   lines: [[{ t: "एक", small: true }, { t: "जीवित", c: "y" }], [{ t: "हृदय", c: "w", big: true }]] },
  { start: 66.68,   out: 70.00,   lines: [[{ t: "5000", c: "w", big: true }, { t: "साल", c: "w" }], [{ t: "आज", small: true }, { t: "भी", small: true }, { t: "धड़कता", c: "y" }, { t: "है", small: true }]] },
  { start: 108.84,  out: 111.80,  lines: [[{ t: "800", c: "w", big: true }, { t: "साल", c: "w" }], [{ t: "पुराना", c: "y" }]] },
  { start: 113.54,  out: 117.00,  lines: [[{ t: "यह", small: true }, { t: "एक", small: true }], [{ t: "यंत्र", c: "y", big: true }, { t: "है", small: true }]] },
  { start: 127.79,  out: 130.80,  lines: [[{ t: "8", c: "r", big: true }, { t: "असंभव", c: "r" }], [{ t: "चीज़ें", c: "y" }]] },
  { start: 131.56,  out: 135.00,  lines: [[{ t: "ब्रह्म", c: "y", big: true }, { t: "पदार्थ", c: "y" }], [{ t: "जीवित", c: "w" }, { t: "हृदय", small: true }]] },
  { start: 146.07,  out: 149.50,  lines: [[{ t: "उल्टा", c: "w" }, { t: "उड़ता", small: true }], [{ t: "ध्वज", c: "y", big: true }]] },
  { start: 152.96,  out: 156.30,  lines: [[{ t: "सुदर्शन", c: "y", big: true }], [{ t: "चक्र", c: "w" }]] },
  { start: 167.37,  out: 170.70,  lines: [[{ t: "उल्टी", c: "y" }, { t: "रसोई", c: "y" }], [{ t: "भौतिकी", c: "w" }, { t: "उलट", c: "r", big: true }]] },
  { start: 179.47,  out: 182.80,  lines: [[{ t: "तीसरी", c: "y" }, { t: "सीढ़ी", c: "y", big: true }], [{ t: "यमराज", c: "r" }, { t: "का", small: true }, { t: "वास", small: true }]] },
  { start: 189.30,  out: 192.70,  lines: [[{ t: "भविष्य", c: "y" }], [{ t: "मालिका", c: "w", big: true }]] },
  { start: 199.47,  out: 202.80,  lines: [[{ t: "कोई", small: true }, { t: "परछाई", c: "y", big: true }], [{ t: "नहीं", c: "w" }]] },
  { start: 209.03,  out: 212.40,  lines: [[{ t: "लहरें", c: "y" }, { t: "रुक", small: true }], [{ t: "जातीं", c: "w", big: true }]] },
  { start: 238.93,  out: 242.30,  lines: [[{ t: "नवकलेवर", c: "y", big: true }], [{ t: "नया", c: "w" }, { t: "शरीर", small: true }]] },
  { start: 341.77,  out: 345.10,  lines: [[{ t: "2015", c: "w", big: true }], [{ t: "दस्ताना", c: "y" }, { t: "फिसला", c: "r" }]] },
  { start: 378.90,  out: 382.30,  lines: [[{ t: "आर्क", c: "y", big: true }, { t: "रिएक्टर?", c: "y" }]] },
  { start: 398.95,  out: 402.30,  lines: [[{ t: "फैराडे", c: "y", big: true }, { t: "केज", c: "y" }], [{ t: "लकड़ी", c: "w" }, { t: "की", small: true }, { t: "मूर्ति", small: true }]] },
  { start: 500.69,  out: 504.10,  lines: [[{ t: "गांधारी", c: "y" }, { t: "का", small: true }], [{ t: "श्राप", c: "r", big: true }]] },
  { start: 651.23,  out: 654.60,  lines: [[{ t: "सबरा", c: "y", big: true }, { t: "जनजाति", c: "y" }], [{ t: "पहले", c: "w" }, { t: "पुजारी", small: true }]] },
  { start: 684.54,  out: 687.90,  lines: [[{ t: "नील", c: "y", big: true }, { t: "माधव", c: "y" }], [{ t: "नीला", c: "w" }, { t: "भगवान", small: true }]] },
  { start: 933.37,  out: 936.70,  lines: [[{ t: "अधूरी", c: "y" }, { t: "मूर्तियाँ", c: "y" }], [{ t: "बिना", c: "w" }, { t: "हाथ-पैर", c: "w", big: true }]] },
  { start: 1071.48, out: 1074.80, lines: [[{ t: "भविष्यवाणी", c: "y" }], [{ t: "कोविड", c: "r", big: true }, { t: "सच", small: true }]] },
  { start: 1123.10, out: 1126.50, lines: [[{ t: "चक्रवात", c: "w" }, { t: "फानी", c: "w" }], [{ t: "चक्र", c: "y", big: true }, { t: "झुका", small: true }]] },
  { start: 1157.74, out: 1161.10, lines: [[{ t: "कल्कि", c: "y", big: true }, { t: "अवतार", c: "y" }], [{ t: "सफ़ेद", c: "w" }, { t: "घोड़े", small: true }, { t: "पर", small: true }]] },
  { start: 1188.61, out: 1192.00, lines: [[{ t: "काला", c: "r", big: true }, { t: "पहाड़", c: "r" }]] },
  { start: 1439.30, out: 1442.70, lines: [[{ t: "215", c: "w", big: true }, { t: "फुट", c: "w" }], [{ t: "नंगे", c: "y" }, { t: "पाँव", small: true }]] },
  { start: 1548.00, out: 1551.40, lines: [[{ t: "752", c: "w", big: true }, { t: "चूल्हे", c: "w" }], [{ t: "सबसे", c: "y" }, { t: "बड़ी", c: "y" }, { t: "रसोई", small: true }]] },
  { start: 1577.11, out: 1580.50, lines: [[{ t: "7", c: "w", big: true }, { t: "बर्तन", c: "w" }], [{ t: "ऊपर", c: "y" }, { t: "वाला", small: true }, { t: "पहले", c: "y" }, { t: "पके", small: true }]] },
  { start: 1695.34, out: 1698.70, lines: [[{ t: "यमशिला", c: "y", big: true }], [{ t: "तीसरी", c: "w" }, { t: "सीढ़ी", small: true }, { t: "वर्जित", c: "r" }]] },
  { start: 1749.95, out: 1753.30, lines: [[{ t: "सुदर्शन", c: "y" }, { t: "चक्र", c: "y" }], [{ t: "2200", c: "w", big: true }, { t: "किलो", c: "w" }]] },
];

const PhraseBlock: React.FC<{ p: Phrase; fps: number }> = ({ p, fps }) => {
  const frame = useCurrentFrame();
  const t = frame / fps;
  const phraseOp = interpolate(t, [p.out - 0.4, p.out], [1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  if (t < p.start - 0.1 || t > p.out + 0.1) return null;
  const gap = p.lineGap ?? LINE_GAP;
  const lh = p.lh ?? LH;
  return (
    <div style={{ position: "absolute", top: PAD, left: PAD, fontFamily, opacity: phraseOp, display: "flex", flexDirection: "column" }}>
      {p.lines.map((line, li) => (
        <div key={li} style={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: 16, marginTop: li === 0 ? 0 : gap }}>
          {line.map((w, gi) => {
            const aT = p.start + (li * 2 + gi) * STAGGER;
            const k = Math.min(Math.max((t - aT) / DUR, 0), 1);
            const ease = 1 - Math.pow(1 - k, 3);
            const op = interpolate(t, [aT, aT + 0.18], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
            const y = -DROP * (1 - ease);
            return (
              <span key={gi} style={{
                color: col(w.c),
                fontSize: w.big ? BIG_FS : w.small ? SMALL_FS : BASE_FS,
                fontWeight: w.big ? 900 : 800,
                lineHeight: lh,
                letterSpacing: p.ls ?? "0",
                textShadow: SH,
                opacity: op,
                transform: `translateY(${y}px)`,
                display: "inline-block",
              }}>{w.t}</span>
            );
          })}
        </div>
      ))}
    </div>
  );
};

// Bottom-left viewing tip — half the size of the date, white, NO holes mask.
const WatchTag: React.FC<{ fps: number; start: number; out: number }> = ({ fps, start, out }) => {
  const t = useCurrentFrame() / fps;
  if (t < start - 0.1 || t > out + 0.1) return null;
  const op = interpolate(t, [start, start + 0.4, out - 0.4, out], [0, 1, 1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <div style={{ position: "absolute", bottom: PAD + 6, left: PAD, fontFamily, opacity: op }}>
      <span style={{ color: WHITE, fontSize: SMALL_FS, fontWeight: 700, letterSpacing: "0.01em", textShadow: SH }}>
        Watch at 1.2x for best experience
      </span>
    </div>
  );
};

// Master comp — holes-masked devotional phrases + non-masked UI (watch tip, LS pills).
export const JagannathOverlaysFull: React.FC<{ phrases?: Phrase[] }> = ({ phrases = JAG_PHRASES }) => {
  const { fps } = useVideoConfig();
  const S = (s: number) => Math.round(s * fps);
  return (
    <AbsoluteFill>
      {/* holes distress mask over the dramatic top-left phrases only */}
      <AbsoluteFill style={{
        backgroundColor: "transparent",
        WebkitMaskImage: `url(${HOLES})`, maskImage: `url(${HOLES})`,
        WebkitMaskSize: "1280px 704px", maskSize: "1280px 704px",
        WebkitMaskRepeat: "no-repeat", maskRepeat: "no-repeat",
        WebkitMaskPosition: "0 0", maskPosition: "0 0",
      }}>
        {phrases.map((p, i) => <PhraseBlock key={i} p={p} fps={fps} />)}
      </AbsoluteFill>

      {/* viewing tip (bottom-left, half-size), shows over the opening */}
      <WatchTag fps={fps} start={1.0} out={6.5} />

      {/* Like+Subscribe — LS2 @ early "सब्सक्राइब" ~101s · LS5 @ outro "सब्सक्राइब" ~1948s */}
      <Sequence from={S(100.86)} durationInFrames={S(5.2)}><DevaKathaLS2 /></Sequence>
      <Sequence from={S(1947.74)} durationInFrames={S(5.2)}><DevaKathaLS5 /></Sequence>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
